// src/components/Layout.tsx
"use client"

import { type ReactNode, useEffect, useState } from "react"
import { usePathname, useRouter } from "next/navigation"
import NProgress from "nprogress"
import "nprogress/nprogress.css"
import { Toaster } from "react-hot-toast"
import { Header } from "./Header"
import useIsCollapsed from "@/hooks/use-is-collapsed"
import { Footer } from "./Footer"
import { QueryClient, QueryClientProvider } from "@tanstack/react-query"
import { AuthProvider } from "../context/AuthContext"
import { CartProvider } from "../context/CartContext"
import { CategoryProvider } from "@/context/CategoryContext"
import { ProductProvider } from "@/context/ProductContext"
import { UserProvider } from "@/context/UserContext"
import { OrderProvider } from "@/context/OrderContext"
import { Navigation } from "./Navigation"

interface LayoutProps {
  children: ReactNode
}

const useTwoFactorAuth = () => {
  const [isEnabled, setIsEnabled] = useState(false)
  useEffect(() => {
    const fetchTwoFactorAuthStatus = async () => {
      try {
        const res = await fetch(`${process.env.NEXT_PUBLIC_API_BASE_URL}/2fa-enabled`)
        if (!res.ok) setIsEnabled(false)
        else {
          const data = await res.json()
          setIsEnabled(data.twoFactorAuth)
        }
      } catch (error) {
        console.error("Error fetching two-factor auth status:", error)
      }
    }
    fetchTwoFactorAuthStatus()
  }, [])
  return isEnabled
}

const Layout = ({ children }: LayoutProps) => {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 60 * 1000, // 1 minute
            refetchOnWindowFocus: false,
          },
        },
      }),
  )
  const pathname = usePathname()
  const router = useRouter()
  const isTwoFactorAuthEnabled = useTwoFactorAuth()
  const [isCollapsed, setIsCollapsed] = useIsCollapsed()
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false)

  // Start/Stop NProgress on route change
  useEffect(() => {
    NProgress.start()
    const timer = setTimeout(() => {
      NProgress.done()
    }, 300) // feel free to tweak duration
    return () => clearTimeout(timer)
  }, [pathname])

  // Close mobile sidebar on large screens
  useEffect(() => {
    const debounce = (func: Function, delay: number) => {
      let timer: NodeJS.Timeout
      return (...args: any[]) => {
        clearTimeout(timer)
        timer = setTimeout(() => func(...args), delay)
      }
    }

    const handleResize = debounce(() => {
      if (window.innerWidth >= 1024 && isMobileSidebarOpen) {
        setIsMobileSidebarOpen(false)
      }
    }, 200)

    window.addEventListener("resize", handleResize)
    return () => window.removeEventListener("resize", handleResize)
  }, [isMobileSidebarOpen])

  const isAdminPage = pathname?.startsWith("/admin")

  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <UserProvider>
          <OrderProvider>
            <ProductProvider>
              <CategoryProvider>
                <CartProvider>
                  {isAdminPage ? (
                    <>{children}</>
                  ) : (
                    <>
                      <Header />
                      <Navigation />
                      <main>{children}</main>
                      <Footer />
                    </>
                  )}

                  <Toaster position="bottom-center" />
                </CartProvider>
              </CategoryProvider>
            </ProductProvider>
          </OrderProvider>
        </UserProvider>
      </AuthProvider>
    </QueryClientProvider>
  )
}

export default Layout
